import { ReactNode } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";
import {
  supplierAdminContactList,
  SupplierAdminContactListItem,
} from "@/app/Suppliers/GraphQL/supplierAdminContactList";
import { useSupplierAdminContactActions } from "@/app/Suppliers/Hooks/useSupplierAdminContactActions";
import { SupplierAdminContactCreateDialog } from "@/app/Suppliers/Components/Dialogs/SupplierAdminContactCreateDialog";
import { SupplierAdminContactUpdateDialog } from "@/app/Suppliers/Components/Dialogs/SupplierAdminContactUpdateDialog";
import { Avatar } from "@/components/catalyst/avatar";
import { getInitials } from "@/lib/Utils/getInitials";
import { getAvatarColorClass } from "@/lib/Utils/getAvatarColorClass";
import { cn } from "@/lib/utils";
import { formatDateTimeSinceNow } from "@/lib/Formatters/formatDateTimeSinceNow";
import { CopyToClipboard } from "@/lib/Components/Common/CopyToClipboard";
import {
  QuerySupplierAdminContactsOrderByColumn,
  SortOrder,
} from "@/gql/graphql";

export function SupplierAdminContactGrid({
  supplierId,
  addLabel = "Add contact",
}: {
  supplierId: string;
  addLabel?: string;
}) {
  const getActions = useSupplierAdminContactActions();
  const { open: openCreate } = useDialog(SupplierAdminContactCreateDialog);
  const { open: openEdit } = useDialog(SupplierAdminContactUpdateDialog);

  const { data } = useGqlQuery(supplierAdminContactList, {
    supplierId,
    page: 1,
    first: 100,
    orderBy: [
      {
        column: QuerySupplierAdminContactsOrderByColumn.Name,
        order: SortOrder.Asc,
      },
    ],
  });

  const contacts = data?.supplierAdminContacts.data ?? [];

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {contacts.map((contact) => (
        <ContactCard
          key={contact.id}
          contact={contact}
          actions={<IconMenuActions actions={getActions(contact)} />}
          onEdit={() => openEdit({ supplierAdminContactId: contact.id })}
        />
      ))}

      <button
        type="button"
        onClick={() => openCreate({ supplierId })}
        className="flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 p-4 text-sm font-semibold text-gray-500 transition hover:border-indigo-300 hover:bg-indigo-50/40 hover:text-indigo-600 focus:outline-none focus-visible:border-indigo-400"
      >
        <PlusIcon className="size-6" />
        {addLabel}
      </button>
    </div>
  );
}

function ContactCard({
  contact,
  actions,
  onEdit,
}: {
  contact: SupplierAdminContactListItem;
  actions: ReactNode;
  onEdit: () => void;
}) {
  return (
    <div className="relative flex flex-col rounded-lg border border-gray-200 bg-white transition focus-within:border-indigo-300 hover:border-gray-300 hover:shadow-sm">
      <button
        type="button"
        onClick={onEdit}
        className="absolute inset-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40"
      >
        <span className="sr-only">Edit {contact.name}</span>
      </button>

      <div className="pointer-events-none relative flex flex-1 flex-col p-4">
        <div className="pointer-events-auto absolute right-2 top-2">
          {actions}
        </div>

        <div className="flex items-center gap-3">
          <Avatar
            className={cn("size-10", getAvatarColorClass(contact.name))}
            initials={getInitials(contact.name)}
          />
          <div className="min-w-0 flex-1">
            <span className="block truncate pr-6 font-semibold text-gray-900">
              {contact.name}
            </span>
            {contact.role ? (
              <span className="block truncate text-sm text-gray-500">
                {contact.role}
              </span>
            ) : null}
          </div>
        </div>

        <div className="mt-2 flex flex-col items-start gap-0.5 text-sm text-gray-700">
          {contact.email ? (
            <CopyToClipboard
              text={contact.email}
              title="Copy email"
              className="pointer-events-auto max-w-full hover:text-indigo-600"
            >
              <span className="truncate">{contact.email}</span>
            </CopyToClipboard>
          ) : null}
          {contact.phone ? (
            <CopyToClipboard
              text={contact.phone}
              title="Copy phone"
              className="pointer-events-auto max-w-full hover:text-indigo-600"
            >
              <span className="truncate">{contact.phone}</span>
            </CopyToClipboard>
          ) : null}
        </div>

        {contact.description ? (
          <p className="mt-2 line-clamp-3 whitespace-pre-line text-sm text-gray-500">
            {contact.description}
          </p>
        ) : null}

        <p className="mt-auto pt-3 text-xs text-gray-400">
          {contact.updated_at && contact.updated_at !== contact.created_at
            ? `Edited ${formatDateTimeSinceNow(contact.updated_at)}`
            : `Added ${formatDateTimeSinceNow(contact.created_at)}`}
        </p>
      </div>
    </div>
  );
}
